<script>
  // Header — balance strip + global action buttons.
  // The balance number is the AGGREGATE across all enabled keys
  // (matches what the user sees in /api/admin/keys summary).
  import { keysApi } from "../api/client.js";
  import PromptsModal from "./PromptsModal.svelte";
  import JobsModal from "./JobsModal.svelte";
  import FilesModal from "./FilesModal.svelte";
  import LayoutEditorModal from "./LayoutEditorModal.svelte";

  let { health, keysSummary, keys, onKeyAdded, onKeyDeleted } = $props();

  let showKeysPanel = $state(false);
  let showPromptsModal = $state(false);
  let showJobsModal = $state(false);
  let showFilesModal = $state(false);
  let showLayoutModal = $state(false);
  let newLabel = $state("");
  let newKey = $state("");
  let addError = $state(null);
  let addBusy = $state(false);

  async function submitNewKey(e) {
    e.preventDefault();
    addError = null;
    addBusy = true;
    try {
      const data = await keysApi.add(newLabel, newKey);
      onKeyAdded?.(data.key);
      newLabel = ""; newKey = "";
    } catch (err) {
      addError = err.message;
    } finally {
      addBusy = false;
    }
  }

  async function deleteKey(k) {
    if (!confirm(`Xóa key "${k.label}" (${k.masked_key})?\n\nKhông thể undo.`)) return;
    try {
      await keysApi.remove(k.id);
      onKeyDeleted?.(k.id);
    } catch (err) {
      alert(`Xóa thất bại: ${err.message}`);
    }
  }

  async function toggleKey(k) {
    try {
      await keysApi.update(k.id, { enabled: !k.enabled });
      // Optimistic update — parent receives the new state via list re-fetch
      k.enabled = !k.enabled;
    } catch (err) {
      alert(`Toggle thất bại: ${err.message}`);
    }
  }
</script>

<header class="app-header">
  <div class="app-header-inner">
    <div class="brand">
      <h1>UI Component Extractor</h1>
      <p class="tagline muted">Detect → Crop → Inpaint qua Gemini (ShopAIKey relay)</p>
    </div>

    <div class="header-meta">
      <!-- Health badge -->
      {#if health}
        <div class="health" data-status={health.ok === false ? "bad" : (health.has_api_key ? "ok" : "warn")}>
          <span class="health-dot"></span>
          <span class="health-text">
            {#if health.ok === false}
              {health.error || "Server down"}
            {:else if !health.has_api_key && keys?.length === 0}
              Chưa có key nào
            {:else}
              API OK
            {/if}
          </span>
        </div>
      {/if}

      <!-- Aggregate balance -->
      {#if keysSummary && keysSummary.count > 0}
        <div class="balance">
          <span class="balance-amount">${keysSummary.balance?.toFixed(2) ?? "—"}</span>
          <span class="balance-meta">{keysSummary.enabled}/{keysSummary.count} keys</span>
        </div>
      {/if}

      <button class="header-btn" onclick={() => (showFilesModal = true)}
              title="Duyệt toàn bộ ảnh đã sinh trong output/">
        🖼️ Files
      </button>
      <button class="header-btn" onclick={() => (showJobsModal = true)}
              title="Lịch sử pipeline jobs (toàn bộ)">
        📋 Jobs
      </button>
      <button class="header-btn" onclick={() => (showLayoutModal = true)}
              title="CC preview layout — chỉnh grid, spacing, kích thước từng element">
        📐 Layout
      </button>
      <button class="header-btn" onclick={() => (showPromptsModal = true)}
              title="Sửa prompts cho từng sheet">
        📝 Prompts
      </button>
      <button class="header-btn" onclick={() => (showKeysPanel = !showKeysPanel)}>
        🔑 Keys ({keys?.length || 0})
      </button>
    </div>
  </div>

  {#if showPromptsModal}
    <PromptsModal {keys} onClose={() => (showPromptsModal = false)} />
  {/if}

  {#if showJobsModal}
    <JobsModal onClose={() => (showJobsModal = false)} />
  {/if}

  {#if showFilesModal}
    <FilesModal onClose={() => (showFilesModal = false)} />
  {/if}

  {#if showLayoutModal}
    <LayoutEditorModal onClose={() => (showLayoutModal = false)} />
  {/if}

  {#if showKeysPanel}
    <div class="keys-panel">
      <div class="keys-panel-section">
        <h4>Thêm key mới</h4>
        <form class="add-key-form" onsubmit={submitNewKey}>
          <input type="text" placeholder="Label (vd: Personal, Backup)" bind:value={newLabel} autocomplete="off" />
          <input type="password" placeholder="sk-..." bind:value={newKey} required autocomplete="off" />
          <button type="submit" class="primary-btn" disabled={addBusy}>
            {addBusy ? "Đang thêm..." : "+ Thêm"}
          </button>
          {#if addError}
            <span class="error-msg">{addError}</span>
          {/if}
        </form>
      </div>

      <div class="keys-panel-section">
        <h4>Keys đã lưu ({keys?.length || 0})</h4>
        {#if !keys || keys.length === 0}
          <p class="muted">Chưa có key nào. Thêm key đầu tiên ở trên.</p>
        {:else}
          <div class="key-list">
            {#each keys as k}
              <div class="key-row" class:disabled={!k.enabled}>
                <span class="key-status" class:enabled={k.enabled}></span>
                <span class="key-row-label">{k.label}</span>
                <code class="key-row-mask">{k.masked_key}</code>
                <span class="key-row-balance">${k.balance?.toFixed(2) ?? "—"}</span>
                <button class="link-btn small" onclick={() => toggleKey(k)} title={k.enabled ? "Tắt" : "Bật"}>
                  {k.enabled ? "⏸" : "▶"}
                </button>
                <button class="link-btn small danger" onclick={() => deleteKey(k)} title="Xóa">×</button>
              </div>
            {/each}
          </div>
        {/if}
      </div>

      <button class="link-btn keys-close" onclick={() => (showKeysPanel = false)}>Đóng</button>
    </div>
  {/if}
</header>

<style>
  .app-header {
    background: var(--bg-elevated);
    border-bottom: 1px solid var(--border);
  }

  .app-header-inner {
    max-width: min(1400px, 96vw);
    margin: 0 auto;
    padding: 14px 20px;
    display: flex;
    align-items: center;
    gap: 20px;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  .brand h1 {
    margin: 0;
    font-size: 1.05rem;
    color: var(--text-strong);
  }

  .tagline {
    margin: 2px 0 0;
    font-size: 0.72rem;
  }

  .header-meta {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .health {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 999px;
    background: var(--panel);
    border: 1px solid var(--border);
    font-size: 0.72rem;
    color: var(--muted-strong);
  }

  .health-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--muted);
  }

  .health[data-status="ok"]   .health-dot { background: var(--success); }
  .health[data-status="warn"] .health-dot { background: var(--warning); }
  .health[data-status="bad"]  .health-dot { background: var(--danger); }

  .balance {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 4px 12px;
    border-radius: 999px;
    background: var(--panel);
    border: 1px solid var(--border);
    font-size: 0.78rem;
  }

  .balance-amount {
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: var(--text);
  }

  .balance-meta {
    color: var(--muted);
    font-size: 0.7rem;
  }

  .header-btn {
    padding: 4px 12px;
    border-radius: 999px;
    background: var(--panel);
    border: 1px solid var(--border);
    font-size: 0.74rem;
    color: var(--muted-strong);
    transition: border-color 0.15s, color 0.15s, background 0.15s;
  }

  .header-btn:hover {
    border-color: var(--accent);
    color: var(--text-strong);
    background: var(--panel-2);
  }

  /* Keys management panel — opens below header when "🔑 Keys" clicked. */
  .keys-panel {
    max-width: min(1400px, 96vw);
    margin: 0 auto;
    padding: 14px 20px;
    background: var(--panel-2);
    border-top: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .keys-panel-section h4 {
    margin: 0 0 8px;
    font-size: 0.78rem;
    color: var(--muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .add-key-form {
    display: grid;
    grid-template-columns: 1fr 2fr auto;
    gap: 8px;
    align-items: center;
  }

  .error-msg {
    grid-column: 1 / -1;
    color: #ff8585;
    font-size: 0.78rem;
  }

  .key-list { display: flex; flex-direction: column; gap: 4px; }

  .key-row {
    display: grid;
    grid-template-columns: 10px 1fr auto auto auto auto;
    gap: 10px;
    align-items: center;
    padding: 6px 10px;
    background: var(--panel);
    border: 1px solid var(--border);
    border-radius: 6px;
    font-size: 0.82rem;
  }
  .key-row.disabled { opacity: 0.5; }

  .key-status {
    width: 8px; height: 8px; border-radius: 50%;
    background: var(--muted);
  }
  .key-status.enabled { background: #4cd197; }

  .key-row-label { color: var(--text-strong); font-weight: 500; }
  .key-row-mask {
    font-family: ui-monospace, monospace; font-size: 0.72rem;
    color: var(--muted); padding: 2px 6px;
    background: var(--bg); border-radius: 4px;
  }
  .key-row-balance {
    font-variant-numeric: tabular-nums;
    color: var(--text);
    font-weight: 500;
  }
  .link-btn.small {
    padding: 2px 8px;
    font-size: 0.9rem;
  }
  .link-btn.danger { color: var(--danger); }
  .link-btn.danger:hover { color: #ffadad; }

  .keys-close { align-self: flex-end; }

  .muted { color: var(--muted); font-size: 0.85rem; margin: 0; }
</style>
